import { effect, inject, Injectable, signal } from '@angular/core';
import { Choice, Customization, FlattenCustomization, OrderSelectedCustomization } from '../../../../../../models/order/api-models/order-pd-response.model';
import { OrderStateServiceV2 } from '../../order-state.service';
import { OrderFlowType, OrderV2 } from '../../../../../../models/order/order.model';
import { OrderCustomizationSubPayload } from '../../../../../../models/order/workflow/order-customization.model';
import { VideoProductResponseService } from '../../order-api-response/video-pd-response.service';
import { AdditionalOfferData } from '../../../../../../models/order/workflow/additional-offer.model';

@Injectable({ providedIn: 'root' })
export class OrderVideoCustomizationsStateService {
    private orderStateService = inject(OrderStateServiceV2);
    private videoPdResponseService = inject(VideoProductResponseService);
    private receiverCountCustomizationCode = 'DTV-RECEIVER-COUNT';
    private receiverCountChoiceCode = 'DTV-RECEIVER-COUNT-NUMBER';

    selectedCustomizations = signal<OrderSelectedCustomization[]>([]);

    constructor() {
        effect(() => {
            const order = this.orderStateService.order();
            const videoPdResponse = this.videoPdResponseService.getSelectedComponent();
            if (order && videoPdResponse) {
                this.setupSelectedCustomizations(order);
            }
        });
    }

    private setupSelectedCustomizations(order: OrderV2) {
        if (order) {
            const selectedCustomizations = [];
            const oneTrustCustomization = this.getOneTrustCustomization(order);
            const edcCustomization = this.getEDCCustomization(order);
            const receiverCountCustomization = this.getReceiverCountCustomization(order);

            if (order.videoFlow?.videoOffers) {
                const videoOffers = order.videoFlow.videoOffers;
                if (videoOffers.offers?.length > 0) {
                    videoOffers.offers
                        .filter(offer => offer.customizationCode !== this.receiverCountCustomizationCode)
                        .forEach(offer => {
                            const selectedCustomization = this.getVideoOfferSelectedCustomization(offer);
                            if (selectedCustomization) {
                                selectedCustomizations.push(selectedCustomization);
                            }
                        });
                }
            }

            if (order.videoFlow?.dtvVideoCommericalSatelliteEquipmentOffers) {
                const equipmentOffers = order.videoFlow.dtvVideoCommericalSatelliteEquipmentOffers;
                if (equipmentOffers.selectedChannels?.length > 0) {
                    equipmentOffers.selectedChannels.filter(x => x.offers.some(y => y.accepted)).map(x => x.offers).flatMap(offers => offers).forEach(offer => {
                        const customization = this.getSelectedCustomization(offer.choiceCode);
                        if (customization) {
                            selectedCustomizations.push(customization);
                        }
                    });
                }
            }

            if (order.videoFlow?.videoAdditionalChannelOffers) {
                order.videoFlow.videoAdditionalChannelOffers.selectedChannels.filter(x => x.offers.some(y => y.accepted)).map(x => x.offers).flatMap(offers => offers).forEach(offer => {
                    const customization = this.getSelectedCustomization(offer.choiceCode);
                    if (customization) {
                        selectedCustomizations.push(customization);
                    }
                });
            }

            if (order.videoFlow?.videoClosingOffers) {
                const closingOffers = order.videoFlow.videoClosingOffers;
                if (closingOffers?.closingOffers?.offers?.length > 0) {
                    closingOffers.closingOffers.offers.filter(offer => offer.accepted).forEach(offer => {
                        const customization = this.getSelectedCustomization(offer.choiceCode);
                        if (customization) {
                            selectedCustomizations.push(customization);
                        }
                    })
                }

                if (closingOffers?.closingAdditionalOffers?.offers?.length > 0) {
                    closingOffers.closingAdditionalOffers.offers.filter(offer => offer.accepted).forEach(offer => {
                        const customization = this.getSelectedCustomization(offer.choiceCode);
                        if (customization) {
                            selectedCustomizations.push(customization);
                        }
                    })
                }
            }

            if (order.videoFlow?.genrePackageSelection) {
                const genrePackageOffer = order.videoFlow.genrePackageSelection.genrePacks;
                if (genrePackageOffer?.offers?.length > 0) {
                    genrePackageOffer.offers.filter(offer => offer.accepted).forEach(offer => {
                        const customization = this.getSelectedCustomization(offer.choiceCode);
                        if (customization) {
                            selectedCustomizations.push(customization);
                        }
                    })
                }

                const miniPackOffer = order.videoFlow.genrePackageSelection.miniPacks;
                if (miniPackOffer?.offers?.length > 0) {
                    miniPackOffer.offers.filter(offer => offer.accepted).forEach(offer => {
                        const customization = this.getSelectedCustomization(offer.choiceCode);
                        if (customization) {
                            selectedCustomizations.push(customization);
                        }
                    })
                }
            }

            if (oneTrustCustomization) {
                selectedCustomizations.push(oneTrustCustomization);
            }

            if (edcCustomization) {
                selectedCustomizations.push(edcCustomization);
            }

            if (receiverCountCustomization) {
                selectedCustomizations.push(receiverCountCustomization);
            }

            selectedCustomizations.sort((a, b) => (a.choice?.CartSeq || 0) - (b.choice?.CartSeq || 0));
            this.selectedCustomizations.set(selectedCustomizations);
        }
    }

    private getVideoOfferSelectedCustomization(offer: AdditionalOfferData): OrderSelectedCustomization | null {
        const customizationReference = this.videoPdResponseService.findCustomizationByChoiceCode(offer.choiceCode);
        if (customizationReference) {
            const choice = offer.type === 'Additional'
                ? customizationReference.customization.Choices?.Choice.find(c => c.ChoiceCode === offer.choiceCode && offer.accepted)
                : customizationReference.customization.Choices?.Choice.find(c => c.ChoiceCode === offer.choiceCode);

            return choice ? {
                type: OrderFlowType.Video,
                choice: choice,
                customizationReference: customizationReference,
                customization: customizationReference.customization,
                value: offer.value,
                usoc: choice.Usoc
            } : null;
        }

        const customization = offer.customizationCode
            ? this.videoPdResponseService.findCustomizationByCode(offer.customizationCode)
            : null;
        const choice = customization?.Choices?.Choice?.find(c => c.ChoiceCode === offer.choiceCode) || offer.choice;

        if (!customization || !choice || (offer.type === 'Additional' && !offer.accepted)) {
            return null;
        }

        return {
            type: OrderFlowType.Video,
            choice: choice,
            customizationReference: {
                customization: customization,
                path: []
            },
            customization: customization
        };
    }

    private getReceiverCountCustomization(order: OrderV2): OrderSelectedCustomization | null {
        const receiverCountOffer = order.videoFlow?.videoOffers?.offers?.find(offer =>
            offer.customizationCode === this.receiverCountCustomizationCode &&
            offer.choiceCode === this.receiverCountChoiceCode
        );
        const isCommercial = order.orderSetup?.orderOptions?.orderType === 'smb';

        if (isCommercial || !this.videoPdResponseService.getDTVProductComponent() || receiverCountOffer?.value == null) {
            return null;
        }

        const choice: Choice = { ChoiceCode: this.receiverCountChoiceCode };
        const customization: Customization = {
            CustomizationCode: this.receiverCountCustomizationCode,
            Choices: { Choice: [choice] }
        };
        const customizationReference: FlattenCustomization = {
            customization,
            path: []
        };

        return {
            type: OrderFlowType.Video,
            choice,
            customizationReference,
            customization,
            value: String(receiverCountOffer.value)
        };
    }

    private getOneTrustCustomization(order: OrderV2): OrderSelectedCustomization | null {
        const oneTrustCustomizationCode = 'VIDEO-DTV-ONETRUST';
        const oneTrustCustomization = order.productSummary?.videoProductSummary?.customerResponse;
        if (oneTrustCustomization?.length > 0) {
            const choice: Choice = {
                ChoiceCode: oneTrustCustomization.toUpperCase(),
            };
            const customization: Customization = {
                CustomizationCode: oneTrustCustomizationCode, Choices: { Choice: [choice] }
            };

            const customizationReference: FlattenCustomization = {
                customization: customization, path: []
            };
            return {
                type: OrderFlowType.Video,
                choice: choice,
                customizationReference: customizationReference,
                customization: customization
            };
        }
        return null;
    }

    private getEDCCustomization(order: OrderV2): OrderSelectedCustomization | null {
        const edcCustomizationCode = 'RTC';
        const edcChoice = order.productSummary?.edcData?.isEDCComplete;

        // Only push customization if accordion was visible and marked complete
        if (!edcChoice) return null;

        const choice: Choice = {
            ChoiceCode: 'YES',
        };
        const customization: Customization = {
            CustomizationCode: edcCustomizationCode,
            Choices: { Choice: [choice] }
        };
        const customizationReference: FlattenCustomization = {
            customization: customization,
            path: []
        };
        return {
            type: OrderFlowType.Video,
            choice: choice,
            customizationReference: customizationReference,
            customization: customization
        };
    }

    private getSelectedCustomization(choiceCode: string): OrderSelectedCustomization | null {
        const customization = this.videoPdResponseService.findCustomizationByChoiceCode(choiceCode);
        if (customization) {
            const choice = customization.customization.Choices?.Choice.find(c => c.ChoiceCode === choiceCode);
            return {
                type: OrderFlowType.Video,
                choice: choice,
                customizationReference: customization,
                customization: customization.customization
            };
        }

        return null;
    }


    buildCustomizationPayload(): OrderCustomizationSubPayload[] | null {
        const videoComponent = this.videoPdResponseService.getSelectedComponent();
        if (!videoComponent) return null;

        const customizations: OrderCustomizationSubPayload[] = [];

        const addCustomization = (customizations: OrderCustomizationSubPayload[], customization: OrderCustomizationSubPayload): void => {
            const existingCustomization = customizations.find(cust => cust.Choice === customization.Choice && cust.Code === customization.Code);
            if (!existingCustomization) {
                customizations.push(customization);
            }
        }

        const findParentCustomizations = (cust: OrderSelectedCustomization, customizations: OrderCustomizationSubPayload[]): boolean => {
            //need a recursvive function to find all parent customizations in customizations array
            let hasFound = false;
            customizations.forEach(element => {
                if (element.Choice === cust.customization.CustomizationCode) {
                    //found the parent customization
                    addCustomization(element.Customizations, {
                        Choice: cust.choice.ChoiceCode || '',
                        Code: cust.customization.CustomizationCode || '',
                        IncludedWithPackage: cust.choice.IncludedWithPackage || '',
                        Value: element.Value,
                        Usoc: element.Usoc,
                    });
                    hasFound = true;
                }

                if (element.Customizations && element.Customizations.length > 0) {
                    const result = findParentCustomizations(cust, element.Customizations);
                    if (result) {
                        hasFound = true;
                    }
                }
            });

            return hasFound;
        };

        this.selectedCustomizations().forEach(selCust => {
            const pathReferences = selCust.customizationReference;
            const isParentFound = findParentCustomizations(selCust, customizations);

            if (isParentFound) return;
            if (!isParentFound && pathReferences.path.length > 1) {
                const rootNode = pathReferences.path[1];
                let rootPayload: OrderCustomizationSubPayload = {
                    Choice: rootNode.type == 'customization' ? (rootNode.customization as Customization).CustomizationCode : (rootNode.customization as Choice).ChoiceCode,
                    Code: pathReferences.path[0].type == 'customization' ? (pathReferences.path[0].customization as Customization).CustomizationCode : (pathReferences.path[0].customization as Choice).ChoiceCode,
                    IncludedWithPackage: (rootNode.customization as Choice).IncludedWithPackage,
                    Customizations: [],
                    Usoc: rootNode.type == 'customization' ? '' : (rootNode.customization as Choice)?.Usoc || ''
                };

                const existingRoot = customizations.find(cust => cust.Choice === rootPayload.Choice);
                if (!existingRoot) {
                    addCustomization(customizations, rootPayload);
                } else {
                    rootPayload = existingRoot;
                }


                for (let index = 2; index < pathReferences.path.length; index++) {
                    const pathNode = pathReferences.path[index];
                    let newRootPayload;
                    if (pathNode.type === 'choice') {
                        newRootPayload = {
                            Choice: (pathNode.customization as Choice).ChoiceCode || '',
                            Code: rootPayload.Code,
                            IncludedWithPackage: (pathNode.customization as Choice).IncludedWithPackage,
                            Customizations: []
                        };
                    } else {
                        const customizationNode = pathNode.customization as Customization;
                        newRootPayload = {
                            Choice: customizationNode.CustomizationCode || '',
                            Code: rootPayload.Code,
                            Customizations: []
                        }
                    }

                    const existingNewRoot = rootPayload.Customizations.find(cust => cust.Code === newRootPayload.Code);
                    if (existingNewRoot) {
                        newRootPayload = existingNewRoot;
                    } else {
                        addCustomization(rootPayload.Customizations, newRootPayload);
                    }
                    rootPayload = newRootPayload;
                }

                addCustomization(rootPayload.Customizations, {
                    Choice: selCust.choice.ChoiceCode || '',
                    IncludedWithPackage: selCust.choice.IncludedWithPackage || '',
                    Code: selCust.customization.CustomizationCode || '',
                    Usoc: selCust.choice.Usoc || '',
                });


                return;
            }

            if (pathReferences.path.length == 1) {
                addCustomization(customizations, {
                    Code: (pathReferences.path[0].customization as Customization).CustomizationCode || '',
                    Choice: '',
                    Customizations: [{
                        Choice: selCust.choice.ChoiceCode || '',
                        Code: selCust.customization.CustomizationCode || '',
                        IncludedWithPackage: selCust.choice.IncludedWithPackage || '',
                        Customizations: [],
                        Usoc: selCust.choice.Usoc || '',
                    }],
                    Usoc: '',

                });

                return
            }
            else {
                addCustomization(customizations, {
                    Choice: selCust.choice.ChoiceCode || '',
                    Code: selCust.customization.CustomizationCode || '',
                    IncludedWithPackage: selCust.choice.IncludedWithPackage || '',
                    Customizations: [],
                    Value: selCust.value,
                    Usoc: selCust.usoc
                });
            }
        });

        return customizations.length > 0 ? customizations : null;
    }
}


